/**
 * Mutation hook for the 請求一覧 button on 統計一覧表 (HIT-4471).
 *
 * 請求一覧 is the corrected per-insurer (本証 vs 医療助成) billing report. It aggregates the
 * whole month onto one page, so the only param is the searched yearmonth. Opens the PDF in a
 * new tab via the preview-ticket flow — mirrors useStatisticalPrintOtherMutation.
 */

import { useMutation } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { createStatisticalBillingListPdfPreviewUrl } from '@/services/main/statisticalReport';
import { getApiErrorMessage } from '@/utils/apiError';

function resolveServiceType(theme: { serviceType?: string } | undefined): string {
  if (theme?.serviceType === SERVICE_TYPE_A2 || theme?.serviceType === SERVICE_TYPE_R2) {
    return theme.serviceType;
  }
  return SERVICE_TYPE_A2;
}

export function useStatisticalPrintBillingMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const toast = useAppToast();

  const { mutateAsync, isPending: isLoading } = useMutation<string, Error, { yearmonth: string }>({
    mutationFn: ({ yearmonth }) =>
      createStatisticalBillingListPdfPreviewUrl(
        tenant,
        clinicNumber,
        yearmonth,
        resolveServiceType(theme),
      ),
    onError: error => {
      toast.error(getApiErrorMessage(error, '印刷に失敗しました。'));
    },
  });

  const triggerPrint = async (yearmonth: string) => {
    const tab = window.open('', '_blank');
    try {
      const url = await mutateAsync({ yearmonth });
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch {
      tab?.close();
    }
  };

  return { triggerPrint, isLoading };
}
